import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';

const PlanWizardModal = ({ 
  isOpen, 
  onClose, 
  routers, 
  onSubmit,
  existingPlans = []
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Paso 1: Tipo de plan
    plan_type: 'time_running',
    
    // Paso 2: Router y Nombre
    router_id: '',
    name: '',
    
    // Paso 3: Duración y Velocidad
    vigencia_dias: '',
    vigencia_horas: '',
    vigencia_minutos: '',
    caducidad_dias: '',
    caducidad_horas: '',
    caducidad_minutos: '',
    data_limit_value: '',
    data_limit_unit: 'MB',
    velocidad_subida: '1',
    velocidad_subida_unit: 'M',
    velocidad_descarga: '1',
    velocidad_descarga_unit: 'M',
    
    // Paso 4: Tipo de usuario
    tipo_usuario: 'pin', // Por defecto PIN
    validate_both_fields: false, // Por defecto solo validar usuario // Por defecto validar en ambos campos
    
    // Paso 5: Configuración de códigos
    usa_prefijo: false,
    prefix: '',
    tipo_codigo: 'alfanumerico',
    longitud_password: '6',
    es_consecutivo: false
  });

  const [errors, setErrors] = useState({});
  const [nameWarning, setNameWarning] = useState('');

  // Resetear wizard cuando se abre
  useEffect(() => {
    if (isOpen) {
      // Resetear al paso 1
      setCurrentStep(1);
      // Resetear formulario completo
      setFormData({
        plan_type: 'time_running',
        router_id: routers && routers.length > 0 ? routers[0].id : '',
        name: '',
        vigencia_dias: '',
        vigencia_horas: '',
        vigencia_minutos: '',
        caducidad_dias: '',
        caducidad_horas: '',
        caducidad_minutos: '',
        data_limit_value: '',
        data_limit_unit: 'MB',
        velocidad_subida: '1',
        velocidad_subida_unit: 'M',
        velocidad_descarga: '1',
        velocidad_descarga_unit: 'M',
        tipo_usuario: 'pin',
        validate_both_fields: false,
        usa_prefijo: false,
        prefix: '',
        tipo_codigo: 'alfanumerico',
        longitud_password: '6',
        es_consecutivo: false
      });
      // Limpiar errores y warnings
      setErrors({});
      setNameWarning('');
    }
  }, [isOpen, routers]);

  // Validar nombre duplicado en tiempo real (Paso 2)
  useEffect(() => {
    if (formData.name && existingPlans.length > 0) {
      const isDuplicate = existingPlans.some(
        plan => plan.name.toLowerCase() === formData.name.toLowerCase() && plan.router_id === formData.router_id
      );
      if (isDuplicate) {
        setNameWarning('⚠️ Ya existe un plan con este nombre. En el resumen podrás elegir actualizarlo o crear uno nuevo.');
      } else {
        setNameWarning('');
      }
    } else {
      setNameWarning('');
    }
  }, [formData.name, formData.router_id, existingPlans]);

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        // Siempre válido, tiene valor por defecto
        break;

      case 2:
        if (!formData.router_id) {
          newErrors.router_id = 'Debes seleccionar un router';
        }
        if (!formData.name || formData.name.trim() === '') {
          newErrors.name = 'El nombre del plan es obligatorio';
        }
        break;

      case 3:
        const dias = parseInt(formData.vigencia_dias) || 0;
        const horas = parseInt(formData.vigencia_horas) || 0;
        const minutos = parseInt(formData.vigencia_minutos) || 0;
        
        // Validar duración (solo para planes de tiempo, no para por consumo)
        if (formData.plan_type !== 'data_limit') {
          if (dias === 0 && horas === 0 && minutos === 0) {
            newErrors.duracion = 'Debes especificar al menos una duración';
          }
        }
        
        // Validar límite de datos (solo para por consumo)
        if (formData.plan_type === 'data_limit') {
          if (!formData.data_limit_value || parseFloat(formData.data_limit_value) <= 0) {
            newErrors.data_limit = 'Debes especificar un límite de datos válido';
          }
        }
        
        // Validar caducidad (solo para tiempo pausado y por consumo)
        if (formData.plan_type === 'time_paused' || formData.plan_type === 'data_limit') {
          const caducidadDias = parseInt(formData.caducidad_dias) || 0;
          const caducidadHoras = parseInt(formData.caducidad_horas) || 0;
          const caducidadMinutos = parseInt(formData.caducidad_minutos) || 0;
          
          if (caducidadDias === 0 && caducidadHoras === 0 && caducidadMinutos === 0) {
            newErrors.caducidad = 'Debes especificar una caducidad máxima';
          }
        }
        
        if (!formData.velocidad_subida || parseFloat(formData.velocidad_subida) <= 0) {
          newErrors.velocidad_subida = 'Velocidad de subida inválida';
        }
        
        if (!formData.velocidad_descarga || parseFloat(formData.velocidad_descarga) <= 0) {
          newErrors.velocidad_descarga = 'Velocidad de bajada inválida';
        }
        break;

      case 4:
        // Siempre válido, tiene valor por defecto
        break;

      case 5:
        if (formData.tipo_usuario === 'userpass' && formData.usa_prefijo && !formData.prefix) {
          newErrors.prefix = 'El prefijo es obligatorio para usuario/contraseña';
        }
        
        if (formData.tipo_usuario === 'pin' && formData.usa_prefijo && !formData.prefix) {
          newErrors.prefix = 'Debes especificar un prefijo';
        }
        
        const longitud = parseInt(formData.longitud_password);
        if (!longitud || longitud < 4 || longitud > 20) {
          newErrors.longitud_password = 'La longitud debe estar entre 4 y 20';
        }
        break;

      case 6:
        // Validación final se hace al enviar
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
      } else {
        // Paso 6: Enviar formulario
        onSubmit(formData);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleClose = () => {
    if (window.confirm('¿Estás seguro de que quieres cancelar? Se perderán todos los datos.')) {
      setCurrentStep(1);
      setFormData({
        plan_type: 'time_running',
        router_id: routers && routers.length > 0 ? routers[0].id : '',
        name: '',
        vigencia_dias: '',
        vigencia_horas: '',
        vigencia_minutos: '',
        caducidad_dias: '',
        caducidad_horas: '',
        caducidad_minutos: '',
        data_limit_value: '',
        data_limit_unit: 'MB',
        velocidad_subida: '1',
        velocidad_subida_unit: 'M',
        velocidad_descarga: '1',
        velocidad_descarga_unit: 'M',
        tipo_usuario: 'pin',
        validate_both_fields: false,
        usa_prefijo: false,
        prefix: '',
        tipo_codigo: 'alfanumerico',
        longitud_password: '6',
        es_consecutivo: false
      });
      setErrors({});
      setNameWarning('');
      onClose();
    }
  };

  const calculateDuration = () => {
    const dias = parseInt(formData.vigencia_dias) || 0;
    const horas = parseInt(formData.vigencia_horas) || 0;
    const minutos = parseInt(formData.vigencia_minutos) || 0;
    return (dias * 24 * 60) + (horas * 60) + minutos;
  };

  const formatDuration = () => {
    const totalMinutes = calculateDuration();
    if (totalMinutes === 0) return 'No especificada';
    
    const dias = Math.floor(totalMinutes / 1440);
    const horas = Math.floor((totalMinutes % 1440) / 60);
    const minutos = totalMinutes % 60;
    
    const parts = [];
    if (dias > 0) parts.push(`${dias} día${dias > 1 ? 's' : ''}`);
    if (horas > 0) parts.push(`${horas} hora${horas > 1 ? 's' : ''}`);
    if (minutos > 0) parts.push(`${minutos} minuto${minutos > 1 ? 's' : ''}`);
    
    return parts.join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex justify-between items-center sticky top-0 z-10">
          <div>
            <h2 className="text-2xl font-bold">Crear Nuevo Plan</h2>
            <p className="text-blue-100 text-sm mt-1">Paso {currentStep} de 6</p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-blue-700 p-2 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 6) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* PASO 1: Tipo de Ficha */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Selecciona tu tipo de ficha</h3>
                <p className="text-gray-600 text-sm">Elige el tipo de plan que mejor se adapte a tus necesidades</p>
              </div>

              <div className="space-y-4">
                {/* Tiempo Corrido */}
                <label className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.plan_type === 'time_running'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="plan_type"
                      value="time_running"
                      checked={formData.plan_type === 'time_running'}
                      onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">⏱️</span>
                        <h4 className="text-lg font-bold text-gray-800">Tiempo Corrido</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        El tiempo corre continuamente desde que el usuario se conecta. No se puede pausar.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Ideal para: Cyber cafés, eventos, uso temporal
                      </p>
                    </div>
                  </div>
                </label>

                {/* Tiempo Pausado */}
                <label className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.plan_type === 'time_paused'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="plan_type"
                      value="time_paused"
                      checked={formData.plan_type === 'time_paused'}
                      onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">⏸️</span>
                        <h4 className="text-lg font-bold text-gray-800">Tiempo Pausado</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        El tiempo se pausa cuando el usuario se desconecta. Puede distribuir su tiempo en varios días.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Ideal para: Hoteles, clientes habituales
                      </p>
                    </div>
                  </div>
                </label>

                {/* Por Consumo */}
                <label className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.plan_type === 'data_limit'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="plan_type"
                      value="data_limit"
                      checked={formData.plan_type === 'data_limit'}
                      onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">📊</span>
                        <h4 className="text-lg font-bold text-gray-800">Por Consumo</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        Se controla por cantidad de MB/GB consumidos. Sin límite de tiempo.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Ideal para: Navegación básica, redes sociales
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* PASO 2: Router y Nombre */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Selecciona el router y nombre del plan</h3>
                <p className="text-gray-600 text-sm">El perfil se creará en el router que selecciones</p>
              </div>

              <div className="space-y-4">
                {/* Router */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Router de Destino *
                  </label>
                  <select
                    value={formData.router_id}
                    onChange={(e) => setFormData({ ...formData, router_id: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {routers && routers.map(router => (
                      <option key={router.id} value={router.id}>
                        {router.name || router.ip_address}
                      </option>
                    ))}
                  </select>
                  {errors.router_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.router_id}</p>
                  )}
                </div>

                {/* Nombre del Plan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Plan *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    placeholder="Ej: 1 HORA WIFI"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                  {nameWarning && (
                    <p className="text-orange-600 text-sm mt-1">{nameWarning}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Tip: Usa un nombre descriptivo que identifique fácilmente el plan
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* PASO 3: Duración y Velocidad */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {formData.plan_type === 'time_running' && 'Duración y Velocidad'}
                  {formData.plan_type === 'time_paused' && 'Duración, Caducidad y Velocidad'}
                  {formData.plan_type === 'data_limit' && 'Límite de Datos, Caducidad y Velocidad'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {formData.plan_type === 'time_running' && 'Configura cuánto tiempo y a qué velocidad navegará el usuario'}
                  {formData.plan_type === 'time_paused' && 'Configura el tiempo de uso, vigencia máxima y velocidad de conexión'}
                  {formData.plan_type === 'data_limit' && 'Configura el límite de datos, vigencia máxima y velocidad de conexión'}
                </p>
              </div>

              {/* Límite de Datos (solo para por consumo) */}
              {formData.plan_type === 'data_limit' && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📊 Límite de Datos *
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    El usuario será desconectado al alcanzar este límite
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="1"
                      value={formData.data_limit_value}
                      onChange={(e) => setFormData({ ...formData, data_limit_value: e.target.value })}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="100"
                    />
                    <select
                      value={formData.data_limit_unit}
                      onChange={(e) => setFormData({ ...formData, data_limit_unit: e.target.value })}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="KB">KB</option>
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>
                  {errors.data_limit && (
                    <p className="text-red-500 text-sm mt-1">{errors.data_limit}</p>
                  )}
                </div>
              )}

              {/* Duración (solo para planes de tiempo) */}
              {formData.plan_type !== 'data_limit' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ⏱️ {formData.plan_type === 'time_running' ? 'Duración del Plan *' : 'Tiempo de Uso *'}
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    {formData.plan_type === 'time_running' 
                      ? 'El tiempo corre continuamente desde que se conecta'
                      : 'Tiempo de uso efectivo (se pausa al desconectar)'}
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Días</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.vigencia_dias}
                        onChange={(e) => setFormData({ ...formData, vigencia_dias: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Horas</label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={formData.vigencia_horas}
                        onChange={(e) => setFormData({ ...formData, vigencia_horas: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Minutos</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formData.vigencia_minutos}
                        onChange={(e) => setFormData({ ...formData, vigencia_minutos: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  {errors.duracion && (
                    <p className="text-red-500 text-sm mt-1">{errors.duracion}</p>
                  )}
                  {calculateDuration() > 0 && (
                    <div className="mt-3 p-3 bg-green-100 rounded-lg">
                      <p className="text-sm text-green-800">
                        📊 Duración total: <span className="font-bold">{formatDuration()}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Caducidad Máxima (solo para tiempo pausado y por consumo) */}
              {(formData.plan_type === 'time_paused' || formData.plan_type === 'data_limit') && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ⏳ Caducidad Máxima *
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    {formData.plan_type === 'time_paused' 
                      ? 'Tiempo máximo total para usar la ficha (ej: 1h de uso distribuido en máximo 7 días)'
                      : 'Tiempo máximo para consumir los datos (ej: 100MB en máximo 3 horas)'}
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Días</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.caducidad_dias}
                        onChange={(e) => setFormData({ ...formData, caducidad_dias: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Horas</label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={formData.caducidad_horas}
                        onChange={(e) => setFormData({ ...formData, caducidad_horas: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Minutos</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formData.caducidad_minutos}
                        onChange={(e) => setFormData({ ...formData, caducidad_minutos: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  {errors.caducidad && (
                    <p className="text-red-500 text-sm mt-1">{errors.caducidad}</p>
                  )}
                </div>
              )}

              {/* Velocidades */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velocidades de Internet
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Subida *</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={formData.velocidad_subida}
                        onChange={(e) => setFormData({ ...formData, velocidad_subida: e.target.value })}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={formData.velocidad_subida_unit}
                        onChange={(e) => setFormData({ ...formData, velocidad_subida_unit: e.target.value })}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="k">Kbps</option>
                        <option value="M">Mbps</option>
                      </select>
                    </div>
                    {errors.velocidad_subida && (
                      <p className="text-red-500 text-xs mt-1">{errors.velocidad_subida}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Bajada *</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={formData.velocidad_descarga}
                        onChange={(e) => setFormData({ ...formData, velocidad_descarga: e.target.value })}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={formData.velocidad_descarga_unit}
                        onChange={(e) => setFormData({ ...formData, velocidad_descarga_unit: e.target.value })}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="k">Kbps</option>
                        <option value="M">Mbps</option>
                      </select>
                    </div>
                    {errors.velocidad_descarga && (
                      <p className="text-red-500 text-xs mt-1">{errors.velocidad_descarga}</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Formato Mikrotik: {formData.velocidad_subida}{formData.velocidad_subida_unit}/{formData.velocidad_descarga}{formData.velocidad_descarga_unit}
                </p>
              </div>
            </div>
          )}

          {/* PASO 4: Tipo de Usuario */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Tipo de Usuario</h3>
                <p className="text-gray-600 text-sm">Elige cómo se autenticarán los usuarios</p>
              </div>

              <div className="space-y-4">
                {/* PIN */}
                <label className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.tipo_usuario === 'pin'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="tipo_usuario"
                      value="pin"
                      checked={formData.tipo_usuario === 'pin'}
                      onChange={(e) => setFormData({ ...formData, tipo_usuario: e.target.value })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-2">PIN (Código Simple)</h4>
                      <p className="text-sm text-gray-600">
                        El usuario solo ingresa un código (ej: ABC123, 123456)
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        ✅ Recomendado para la mayoría de casos
                      </p>
                    </div>
                  </div>
                </label>

                {/* Usuario/Contraseña */}
                <label className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.tipo_usuario === 'userpass'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="radio"
                      name="tipo_usuario"
                      value="userpass"
                      checked={formData.tipo_usuario === 'userpass'}
                      onChange={(e) => setFormData({ ...formData, tipo_usuario: e.target.value })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-2">Usuario/Contraseña</h4>
                      <p className="text-sm text-gray-600">
                        El usuario ingresa usuario y contraseña por separado
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Útil para usuarios habituales o control de acceso más estricto
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* PASO 5: Configuración de Códigos */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Configuración de Códigos</h3>
                <p className="text-gray-600 text-sm">
                  {formData.tipo_usuario === 'pin' ? 'Personaliza cómo se generan los códigos PIN' : 'Personaliza cómo se generan usuarios y contraseñas'}
                </p>
              </div>

              {/* Prefijo */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="usa_prefijo"
                    checked={formData.usa_prefijo}
                    onChange={(e) => setFormData({ ...formData, usa_prefijo: e.target.checked, prefix: e.target.checked ? formData.prefix : '' })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="usa_prefijo" className="text-sm font-medium text-gray-700">
                    Usar prefijo {formData.tipo_usuario === 'userpass' && formData.usa_prefijo && '(obligatorio para Usuario/Contraseña)'}
                  </label>
                </div>

                {formData.usa_prefijo && (
                  <div>
                    <input
                      type="text"
                      value={formData.prefix}
                      onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                      placeholder="Ej: WIFI"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.prefix && (
                      <p className="text-red-500 text-sm mt-1">{errors.prefix}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.tipo_usuario === 'pin' 
                        ? '💡 Ejemplo: Con prefijo "WIFI" → Código: WIFI123456'
                        : '💡 Ejemplo: Con prefijo "WIFI" → Usuario: WIFI001, WIFI002...'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Validar en ambos campos (solo para PIN) */}
              {formData.tipo_usuario === 'pin' && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="validate_both_fields"
                      checked={formData.validate_both_fields}
                      onChange={(e) => setFormData({ ...formData, validate_both_fields: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="validate_both_fields" className="text-sm font-medium text-gray-700">
                      Validar en ambos campos (Usuario y Contraseña)
                    </label>
                  </div>
                  <div className={`p-3 rounded-lg border ${formData.validate_both_fields ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                    <p className="text-xs text-gray-700">
                      {formData.validate_both_fields ? (
                        <>
                          ✅ <strong>Activado:</strong> El código se validará como usuario Y contraseña<br/>
                          <span className="font-mono text-xs">Mikrotik → Usuario: ABC123, Contraseña: ABC123</span>
                        </>
                      ) : (
                        <>
                          ⚠️ <strong>Desactivado:</strong> Solo se validará el usuario<br/>
                          <span className="font-mono text-xs">Mikrotik → Usuario: ABC123, Contraseña: (vacío)</span>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-blue-700 mt-2">
                      💡 Recomendado: Activado para portales captive estándar
                    </p>
                  </div>
                </div>
              )}

              {/* Consecutivos (solo para userpass con prefijo) */}
              {formData.tipo_usuario === 'userpass' && formData.usa_prefijo && (
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="es_consecutivo"
                      checked={formData.es_consecutivo}
                      onChange={(e) => setFormData({ ...formData, es_consecutivo: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="es_consecutivo" className="text-sm font-medium text-gray-700">
                      Generar usuarios consecutivos
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    💡 Ejemplo: WIFI001, WIFI002, WIFI003...
                  </p>
                </div>
              )}

              {/* Tipo de Código */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Código {formData.tipo_usuario === 'userpass' && '(para contraseña)'}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer ${
                    formData.tipo_codigo === 'numerico' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="tipo_codigo"
                      value="numerico"
                      checked={formData.tipo_codigo === 'numerico'}
                      onChange={(e) => setFormData({ ...formData, tipo_codigo: e.target.value })}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Numérico</div>
                      <div className="text-xs text-gray-500">Solo números: 123456</div>
                    </div>
                  </label>

                  <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer ${
                    formData.tipo_codigo === 'alfanumerico' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="tipo_codigo"
                      value="alfanumerico"
                      checked={formData.tipo_codigo === 'alfanumerico'}
                      onChange={(e) => setFormData({ ...formData, tipo_codigo: e.target.value })}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Alfanumérico</div>
                      <div className="text-xs text-gray-500">Letras + números: AB12CD</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Longitud */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitud del Código
                </label>
                <select
                  value={formData.longitud_password}
                  onChange={(e) => setFormData({ ...formData, longitud_password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="4">4 caracteres</option>
                  <option value="6">6 caracteres</option>
                  <option value="8">8 caracteres</option>
                  <option value="10">10 caracteres</option>
                  <option value="12">12 caracteres</option>
                </select>
                {errors.longitud_password && (
                  <p className="text-red-500 text-sm mt-1">{errors.longitud_password}</p>
                )}
              </div>

              {/* Vista Previa */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Vista Previa del Código:</p>
                <div className="font-mono text-lg text-blue-600">
                  {formData.usa_prefijo && formData.prefix ? `${formData.prefix}` : ''}
                  {formData.tipo_codigo === 'numerico' ? '123456'.slice(0, parseInt(formData.longitud_password)) : 'AB12CD'.slice(0, parseInt(formData.longitud_password))}
                </div>
              </div>
            </div>
          )}

          {/* PASO 6: Resumen */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Resumen del Plan</h3>
                <p className="text-gray-600 text-sm">Revisa todos los datos antes de crear el plan</p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
                <h4 className="text-2xl font-bold text-blue-900 mb-4">{formData.name}</h4>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Tipo de Plan:</span>
                    <span className="text-gray-900">
                      {formData.plan_type === 'time_running' && '⏱️ Tiempo Corrido'}
                      {formData.plan_type === 'time_paused' && '⏸️ Tiempo Pausado'}
                      {formData.plan_type === 'data_limit' && '📊 Por Consumo'}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Router:</span>
                    <span className="text-gray-900">
                      {routers && routers.find(r => r.id === formData.router_id)?.name || routers.find(r => r.id === formData.router_id)?.ip_address}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Duración:</span>
                    <span className="text-gray-900 font-bold">{formatDuration()}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Velocidad:</span>
                    <span className="text-gray-900">
                      {formData.velocidad_subida}{formData.velocidad_subida_unit} ↑ / {formData.velocidad_descarga}{formData.velocidad_descarga_unit} ↓
                    </span>
                  </div>

                  <div className="border-t border-blue-300 my-3"></div>

                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Tipo de Usuario:</span>
                    <span className="text-gray-900">
                      {formData.tipo_usuario === 'pin' ? '🔑 PIN' : '👤 Usuario/Contraseña'}
                    </span>
                  </div>

                  {formData.usa_prefijo && (
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Prefijo:</span>
                      <span className="text-gray-900 font-mono">{formData.prefix}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Tipo de Código:</span>
                    <span className="text-gray-900">
                      {formData.tipo_codigo === 'numerico' ? 'Numérico' : 'Alfanumérico'} ({formData.longitud_password} caracteres)
                    </span>
                  </div>

                  {formData.tipo_usuario === 'userpass' && formData.es_consecutivo && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-700">Usuarios consecutivos activado</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Importante:</strong> Al crear este plan se generará el perfil en el router Mikrotik con autoeliminación completa.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer con botones */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center sticky bottom-0">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Regresar
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {currentStep === 6 ? (
                <>
                  <Check className="w-4 h-4" />
                  Crear Plan
                </>
              ) : (
                <>
                  Siguiente
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanWizardModal;
